import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedPortalRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

export const ProtectedPortalRoute: React.FC<ProtectedPortalRouteProps> = ({ 
  children, 
  allowedRoles 
}) => {
  const { portalUser, loading } = usePortalAuth();
  const { user, userRole } = useAuth();
  const location = useLocation();
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Failsafe timer to prevent hanging loading screen
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        console.warn('Loading timeout reached, proceeding with app');
        setHasTimedOut(true);
      }
    }, 7000);

    return () => clearTimeout(timer);
  }, [loading]);

  if (loading && !hasTimedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  // Check if this is a CRM user trying to access portal routes
  if (user && userRole && ['admin', 'super_admin', 'crm_user'].includes(userRole)) {
    return <Navigate to="/crm" replace />;
  }

  // Not logged in
  if (!portalUser) {
    // Determine which auth page based on the first allowed role
    const authPath = allowedRoles.includes('inspector') 
      ? '/inspector/auth' 
      : '/client/auth';
    return <Navigate to={authPath} state={{ from: location }} replace />;
  }

  // Check if user has the correct role
  if (!allowedRoles.includes(portalUser.role)) {
    // Wrong portal role - redirect to their own portal
    const redirectPath = portalUser.role === 'inspector' ? '/inspector' : '/client';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};
